<template>
  <gg-card>
    <gg-card-header>
      <template v-if="edit">Edit Autoresponder</template>
      <template v-else>Create New Autoresponder</template>
    </gg-card-header>
    <gg-card-text>
      <v-form @submit.prevent="newResponder">
        <v-container fluid>
          <v-row>
            <!-- Email -->
            <v-col cols="12" sm="6" class="pb-0">
              <gg-text-field
                outlined
                label="Email"
                placeholder="For example: info"
                type="email"
                v-model="model.email"
                v-validate="rules.email"
                data-vv-name="email"
                data-vv-as="Email"
                :error-messages="errors.collect('email')"
              >
                <template v-slot:append>
                  <div class="append-bg">
                    <domain-select no-label
                                   hide-details
                                   solo
                                   flat
                                   v-model="model.domain"
                                   :remote_domain="model.domain"
                                   prepend-inner-icon="mdi-at"
                                   style="margin-top: 10px"
                    />
                  </div>
                </template>
              </gg-text-field>

            </v-col>
            <!-- From -->
            <v-col cols="12" sm="6" class="pb-0">
              <gg-text-field placeholder="From"
                             outlined
                             label="From"
                             v-model="model.from"
                             v-validate="rules.from"
                             data-vv-name="from"
                             data-vv-as="From"
                             :error-messages="errors.collect('from')"
              />
            </v-col>
            <!-- Subject -->
            <v-col cols="12" class="py-0">
              <gg-text-field placeholder="Subject"
                             outlined
                             label="Subject"
                             v-model="model.subject"
                             v-validate="rules.subject"
                             data-vv-name="subject"
                             data-vv-as="Subject"
                             :error-messages="errors.collect('subject')"
              />
            </v-col>
            <!-- Body -->
            <v-col cols="12" class="py-0">
              <label class="d-block pb-2">Body</label>
              <v-textarea placeholder="Body"
                          dense
                          outlined
                          hide-details
                          v-model="model.body"
                          v-validate="rules.body"
                          data-vv-name="body"
                          data-vv-as="Message body"
                          :error-messages="errors.collect('body')"
              />
            </v-col>
            <!-- Contains html -->
            <v-col cols="12" class="pt-0 pb-6">
              <v-checkbox
                label="This message contains HTML"
                v-model="model.isHtml"
                hide-details
              />
            </v-col>
            <!-- Start date -->
            <v-col cols="12">
              <label class="d-block pb-2">
                Select Start Date & Time
              </label>
              <v-row v-if="model.start">
                <v-col cols="12" md="3">
                  <v-menu v-model="dateStartModal"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <gg-text-field v-model="dateStart.date"
                                    placeholder="Select Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-validate="'required'"
                                    data-vv-name="Start Date"
                                    data-vv-as="Start Date"
                                    :error-messages="errors.collect('Start Date')"
                                    v-bind="attrs"
                                    v-on="on"
                      />
                    </template>
                    <v-date-picker v-model="dateStart.date"
                                   class="picker-td"
                                   :min="currentDate"
                                   @input="dateStartModal = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu ref="startTime"
                          v-model="timeStartModal"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="dateStart.time"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <gg-text-field v-model="dateStart.time"
                                    placeholder="Select Start Time"
                                    prepend-icon="mdi-clock-outline"
                                    readonly
                                    v-validate="'required'"
                                    data-vv-name="Start Time"
                                    data-vv-as="Start Time"
                                    :error-messages="errors.collect('Start Time')"
                                    v-bind="attrs"
                                    v-on="on"
                      />
                    </template>
                    <v-time-picker v-if="timeStartModal"
                                   v-model="dateStart.time"
                                   full-width
                                   @click:minute="$refs.startTime.save(dateStart.time)"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
            <!-- Stop date -->
            <v-col cols="12">
              <label class="d-block pb-2">
                Select End Date & Time
              </label>
              <v-row v-if="model.stop">
                <v-col cols="12" md="3">
                  <v-menu v-model="dateEndModal"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <gg-text-field v-model="dateEnd.date"
                                    placeholder="Select Stop Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-validate="'required'"
                                    data-vv-name="Stop Date"
                                    data-vv-as="Stop Date"
                                    :error-messages="errors.collect('Stop Date')"
                                    v-bind="attrs"
                                    v-on="on"
                      />
                    </template>
                    <v-date-picker v-model="dateEnd.date"
                                   :min="currentDate"
                                   class="picker-td"
                                   @input="dateEndModal = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu ref="endTime"
                          v-model="timeEndModal"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="dateEnd.time"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <gg-text-field v-model="dateEnd.time"
                                    placeholder="Select Stop Time"
                                    prepend-icon="mdi-clock-outline"
                                    readonly
                                    v-validate="'required'"
                                    data-vv-name="Stop Time"
                                    data-vv-as="Stop Time"
                                    :error-messages="errors.collect('Stop Time')"
                                    v-bind="attrs"
                                    v-on="on"
                      />
                    </template>
                    <v-time-picker v-if="timeEndModal"
                                   v-model="dateEnd.time"
                                   full-width
                                   @click:minute="$refs.endTime.save(dateEnd.time)"
                    />
                  </v-menu>
                </v-col>
              </v-row>
              <!--              <g-g-date-time-picker v-if="model.stop"-->
              <!--                                    label="Select Stop time"-->
              <!--                                    v-model="model.stopRawTime"-->
              <!--                                    outlined-->
              <!--                                    v-bind="datePickerProps"-->

              <!--              />-->
              <v-alert type="warning" dense v-if="!isRangeCorrect">
                {{ error_msg }}
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-row>
                <v-col cols="12" md="3" lg="2">
                  <gg-input-label>Time Delay</gg-input-label>
                  <v-text-field outlined
                                dense
                                hide-details
                                v-model="model.interval"
                                v-validate="rules.interval"
                                data-vv-name="interval"
                                data-vv-as="Interval"
                                :error-messages="errors.collect('interval')"
                  >
                    <template v-slot:append-outer>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on">
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        Hours to wait before sending autoresponder to the same address
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-card-actions>
        <gg-btn secondary @click="applyInitialModel">
          Cancel
        </gg-btn>
        <gg-btn primary :loading="loading" @click="newResponder">
          <template v-if="edit">Update</template>
          <template v-else>Create</template>
        </gg-btn>
      </v-card-actions>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { scrollTo } from 'Helpers/helpers'
import format from 'date-fns/format'
import getUnixTime from 'date-fns/getUnixTime'
import fromUnixTime from 'date-fns/fromUnixTime'
import parseISO from 'date-fns/parseISO'
import DomainSelect from '@/components/Common/DomainSelect'

export default {
  name: 'CreateNewResponder',
  components: {
    DomainSelect,
  },
  props: {
    edit: {
      type: Boolean,
      required: true,
      default: false,
    },
    email: {
      type: String
    },
    domain: {
      type: String
    }
  },
  data: () => ({
    dateStart: {
      date: '',
      time: '',
    },
    dateEnd: {
      date: '',
      time: '',
    },
    initialModel: {
      interval: '24',
      email: '',
      charset: '',
      domain: '',
      from: '',
      subject: '',
      isHtml: false,
      body: '',
      start: true,
      stop: true,
      startRawTime: null,
      stopRawTime: null
    },
    model: {
      interval: '24',
      email: '',
      charset: 'utf-8',
      domain: '',
      from: '',
      subject: '',
      isHtml: false,
      body: '',
      start: true,
      stop: true,
      startRawTime: null,
      stopRawTime: null
    },
    dateStartModal: false,
    timeStartModal: false,
    dateEndModal: false,
    timeEndModal: false,
    loading: false,
    rules: {
      interval: {
        required: true,
        integer: true
      },
      email: {
        required: true,
      },
      from: {
        required: true
      },
      subject: {
        required: true
      },
      body: {
        required: true
      }
    },
    error_msg: 'The stop time must be later than the start time.',
  }),
  computed: {
    ...mapGetters({
      responders: 'email/responders'
    }),
    currentDate () {
      return new Date().toISOString()
    },
    datePickerProps () {
      return {
        datePickerProps: {
          min: this.currentDate
        },
        textFieldProps: {
          outlined: true
        }
      }
    },
    startTimeFormatted () {
      if (this.model.startRawTime === 0) {
        return 0
      } else {
        const time = typeof this.model.startRawTime === 'string'
          ? this.model.startRawTime.includes(':') ? this.model.startRawTime : this.model.startRawTime.replace(' ', '')
          : this.model.startRawTime
        const date = new Date(time)
        return this.getUnixTime(date)
      }
    },
    stopTimeFormatted () {
      if (this.model.stopRawTime === 0) {
        return 0
      } else {
        const time = typeof this.model.stopRawTime === 'string'
          ? this.model.stopRawTime.includes(':') ? this.model.stopRawTime : this.model.stopRawTime.replace(' ', '')
          : this.model.stopRawTime
        const date = new Date(time)
        return this.getUnixTime(date)
      }
    },
    isRangeCorrect () {
      if (this.startTimeFormatted > 0) {
        return this.stopTimeFormatted > this.startTimeFormatted
      } else {
        return true
      }
    }
  },
  watch: {
    // Immediate, so a form mounted straight into edit mode loads the rule.
    edit: {
      immediate: true,
      handler (val) {
        if (val) {
          this.editExisting()
        }
      }
    },
    dateStart: {
      deep: true,
      handler (val) {
        if (val.date !== '' || val.time !== '') {
          this.model.startRawTime = val.date.concat(' ', val.time)
        }
      }
    },
    dateEnd: {
      deep: true,
      handler (val) {
        if (val.date !== '' || val.time !== '') {
          this.model.stopRawTime = val.date.concat(' ', val.time)
        }
      }
    }
  },
  methods: {
    format,
    getUnixTime,
    fromUnixTime,
    parseISO,
    scrollTo,
    ...mapActions({
      addResponder: 'email/addResponder',
      editResponder: 'email/editResponder',
      setRespondersDomain: 'email/setRespondersDomain',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError'
    }),
    setDomain (domain) {
      this.model.domain = domain
    },
    async newResponder () {
      try {
        const valid = await this.$validator.validateAll()
        if (!valid || !this.isRangeCorrect) {
          return
        }

        this.loading = true
        const data = {
          uapi_module: 'Email',
          uapi_function: 'add_auto_responder',
          charset: this.model.charset,
          interval: parseFloat(this.model.interval),
          email: this.model.email,
          domain: this.model.domain,
          from: this.model.from,
          subject: this.model.subject,
          is_html: this.model.isHtml,
          body: this.model.body,
          start: this.model.start ? this.startTimeFormatted : '',
          stop: this.model.stop ? this.stopTimeFormatted : '',
          _username: this.$route.query.user,
        }
        await this.addResponder(data)
        this.setRespondersDomain(data.domain)

        this.reportSuccess('The autoresponder was successfully created.')
        this.applyInitialModel()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loading = false
      }
    },
    applyInitialModel () {
      Object.assign(this.model, this.initialModel)
      this.dateStart.date = ''
      this.dateStart.time = ''
      this.dateEnd.date = ''
      this.dateEnd.time = ''

      this.$validator.reset()
      this.$emit('change')
    },
    async editExisting () {
      try {
        const data = {
          uapi_module: 'Email',
          uapi_function: 'get_auto_responder',
          email: this.email,
          _username: this.$route.query.user,
        }
        await this.editResponder(data)

        this.model.charset = this.responders.responder.charset
        this.model.interval = this.responders.responder.interval
        this.model.email = this.email.slice(0, this.email.search('@'))
        this.model.domain = this.email.slice(this.email.search('@') + 1)
        this.model.from = this.responders.responder.from
        this.model.subject = this.responders.responder.subject
        this.model.isHtml = this.responders.responder.is_html
        this.model.body = this.responders.responder.body
        this.model.start = !!this.responders.responder.start
        this.model.stop = !!this.responders.responder.stop

        const timeStart = format(this.fromUnixTime(this.responders.responder.start), 'yyyy-MM-dd HH:mm').split(' ')
        const timeEnd = format(this.fromUnixTime(this.responders.responder.stop), 'yyyy-MM-dd HH:mm').split(' ')

        this.dateStart.date = timeStart[0]
        this.dateStart.time = timeStart[1]

        this.dateEnd.date = timeEnd[0]
        this.dateEnd.time = timeEnd[1]

        // this.model.startRawTime = (format(this.responders.responder.start, 'yyyy-MM-dd HH:mm'))
        // this.model.stopRawTime = (format(this.responders.responder.stop, 'yyyy-MM-dd HH:mm'))
        // this.model.stopRawTime = (this.responders.responder.stop * 1000)
        await this.scrollTo('body')
      } catch (e) {
        this.reportError(e)
      }
    },
    actionCompleted () {
      this.$emit('action-completed')
    }
  },
}
</script>

<style lang="scss">
.append-bg {
  position: absolute;
  right: 2px;
  top: 2px;
  background: #ececec;
  height: calc(100% - 4px);
  border-radius: 3px;
  > div {
    margin-top: 3px !important;
  }
}
.picker-td {
  table {
    tr {
      td {
        height: initial !important;
      }
    }
  }
}
</style>
